import { NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/admin/AuthContext";
import { BirdMark } from "@/components/brand";
import { LayoutGrid, Wallet, Settings, LogOut, KeyRound } from "lucide-react";
import { useState } from "react";
import { api, apiError } from "@/admin/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const links = [
  { to: "/admin", label: "Projects", icon: LayoutGrid, end: true },
  { to: "/admin/payments", label: "Payments", icon: Wallet },
  { to: "/admin/templates", label: "Templates", icon: Settings },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);

  if (user === null) {
    return <div className="flex min-h-screen items-center justify-center bg-cream text-mutedink">Loading…</div>;
  }
  if (user === false) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <div className="flex min-h-screen bg-cream">
      {/* Sidebar */}
      <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col bg-navy-dark px-4 py-6 md:flex">
        <div className="mb-8 flex items-center gap-2 px-2">
          <BirdMark className="h-9 w-9" alt="Sankofa" />
          <div>
            <div className="font-sans text-sm font-700 tracking-[0.16em] text-white">SANKOFA</div>
            <div className="text-[0.55rem] uppercase tracking-[0.28em] text-gold">Console</div>
          </div>
        </div>
        <nav className="flex-1 space-y-1">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} data-testid={`sidebar-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-white/10 text-gold" : "text-white/75 hover:bg-white/5 hover:text-white"}`}>
              <l.icon className="h-4 w-4" /> {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-4 border-t border-white/10 pt-4">
          <div className="px-3 text-xs text-white/70">{user.name}</div>
          <div className="px-3 text-[0.65rem] text-white/40">{user.email}</div>
          <button onClick={() => setPwOpen(true)} className="mt-3 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-white/75 hover:bg-white/5">
            <KeyRound className="h-4 w-4" /> Change password
          </button>
          <button onClick={logout} data-testid="logout-btn" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-white/75 hover:bg-white/5">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="flex flex-1 flex-col">
        <div className="flex items-center justify-between border-b border-navy/10 bg-navy-dark px-4 py-3 md:hidden">
          <div className="flex items-center gap-2">
            <BirdMark className="h-7 w-7" alt="Sankofa" />
            <span className="font-sans text-sm font-700 tracking-[0.16em] text-white">SANKOFA</span>
          </div>
          <div className="flex gap-3">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} className={({ isActive }) => `text-xs ${isActive ? "text-gold" : "text-white/70"}`}>{l.label}</NavLink>
            ))}
            <button onClick={logout} className="text-xs text-white/70">Exit</button>
          </div>
        </div>
        <main className="flex-1 p-4 md:p-8">
          <Outlet />
        </main>
      </div>

      <ChangePasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} />
    </div>
  );
}

function ChangePasswordDialog({ open, onClose }) {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const save = async () => {
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password updated");
      setCur(""); setNw(""); onClose();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>Change password</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input type="password" placeholder="Current password" value={cur} onChange={(e) => setCur(e.target.value)} />
          <Input type="password" placeholder="New password (min 6 chars)" value={nw} onChange={(e) => setNw(e.target.value)} />
        </div>
        <DialogFooter>
          <button onClick={save} className="rounded-full bg-gold px-5 py-2 font-600 text-navy-dark">Update</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
